import React, { useMemo } from 'react';

import { useLocation, useNavigate } from 'react-router';

import { Alert, Box, CircularProgress, Typography } from '@mui/material';

import { useCsAgentRoster } from '~/hooks/apis/reporting/useCsAgentRoster';
import { useCsDashboardStats } from '~/hooks/apis/reporting/useCsDashboardStats';
import useUrlQuery from '~/hooks/useQuery';

import CsDashboardAgentTable from './CsDashboardAgentTable';
import CsDashboardChart from './CsDashboardChart';
import CsDashboardFilters from './CsDashboardFilters';
import {
  buildChartDataForFilteredTeam,
  buildChartDataFromAgentRows,
  buildCsAgentRosterRows,
  buildMergedAgentRows,
  computeMaxCellCount,
  getAllDates,
} from './csDashboardUtils';
import { CS_FEATURE_MAP, DEFAULT_CS_FEATURE_IDS, MAX_SELECTED_CS_FEATURES } from './features/registry';

export default function CsDashboardContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const urlQuery = useUrlQuery();

  const selectedTeamLeader = urlQuery.get('teamLeader') || '';
  const startAt = urlQuery.get('start_at') || '';
  const endAt = urlQuery.get('end_at') || '';

  const rawFeatures = urlQuery.get('features');
  const selectedFeatureIds: string[] = rawFeatures
    ? rawFeatures
        .split(',')
        .filter((id) => CS_FEATURE_MAP[id])
        .slice(0, MAX_SELECTED_CS_FEATURES)
    : DEFAULT_CS_FEATURE_IDS;

  function updateUrlParams(updates: Record<string, string>) {
    const params = new URLSearchParams(location.search);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    });
    navigate({ pathname: location.pathname, search: params.toString() ? `?${params}` : '' }, { replace: true });
  }

  const { data, isLoading, error } = useCsDashboardStats({
    featureIds: selectedFeatureIds,
    startAt: startAt || undefined,
    endAt: endAt || undefined,
  });

  const { data: rosterData } = useCsAgentRoster();
  const roster = rosterData?.result.agents;
  const teamLeaders = rosterData?.result.teamLeaders ?? [];

  const rosterRows = useMemo(() => buildCsAgentRosterRows(roster ?? []), [roster]);
  const allAgentRows = useMemo(
    // Only merge against the roster once it has loaded
    () => buildMergedAgentRows(data?.result.features, roster ? rosterRows : undefined),
    [data, roster, rosterRows],
  );

  const filteredAgentEmails = useMemo<Set<string> | null>(() => {
    if (!selectedTeamLeader) return null;
    const emails = new Set<string>();
    allAgentRows.forEach((row) => {
      if (row.teamLeader === selectedTeamLeader) {
        emails.add(row.agentEmail);
      }
    });
    return emails;
  }, [selectedTeamLeader, allAgentRows]);

  const visibleRows = useMemo(() => {
    if (!filteredAgentEmails) return allAgentRows;
    return allAgentRows.filter((row) => filteredAgentEmails.has(row.agentEmail));
  }, [allAgentRows, filteredAgentEmails]);

  const chartData = useMemo(
    () =>
      filteredAgentEmails
        ? buildChartDataForFilteredTeam(selectedFeatureIds, allAgentRows, filteredAgentEmails)
        : buildChartDataFromAgentRows(selectedFeatureIds, allAgentRows),
    [filteredAgentEmails, selectedFeatureIds, allAgentRows],
  );

  const allDates = useMemo(() => getAllDates(allAgentRows, selectedFeatureIds), [allAgentRows, selectedFeatureIds]);

  const maxCellCount = useMemo(
    () => computeMaxCellCount(visibleRows, selectedFeatureIds, allDates),
    [visibleRows, selectedFeatureIds, allDates],
  );

  const chartTitle = `Daily feature usage — total across ${selectedTeamLeader ? selectedTeamLeader : 'all agents'}`;

  return (
    <>
      <Box mt={4}>
        <Alert severity="info" variant="outlined">
          <Typography variant="body1">
            Tracks CS agent adoption of new features. Data is sourced from Snowplow events via BigQuery and refreshed
            periodically by the analytics pipeline — expect a few hours of latency between an event firing and it
            appearing here.
          </Typography>
        </Alert>
      </Box>

      <CsDashboardFilters
        selectedFeatureIds={selectedFeatureIds}
        selectedTeamLeader={selectedTeamLeader}
        startAt={startAt}
        endAt={endAt}
        teamLeaders={teamLeaders}
        updateUrlParams={updateUrlParams}
      />

      {error && (
        <Box mt={4}>
          <Alert severity="error">
            Error loading dashboard stats: {error instanceof Error ? error.message : 'Unknown error'}
          </Alert>
        </Box>
      )}

      {isLoading && (
        <Box mt={4} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      )}

      {!isLoading && !error && data && (
        <CsDashboardChart chartData={chartData} selectedFeatureIds={selectedFeatureIds} title={chartTitle} />
      )}

      {!isLoading && !error && data && visibleRows.length > 0 && (
        <CsDashboardAgentTable
          visibleRows={visibleRows}
          selectedFeatureIds={selectedFeatureIds}
          allDates={allDates}
          maxCellCount={maxCellCount}
        />
      )}
    </>
  );
}
